import React, { useState, useEffect } from 'react';
import { Users, Download, Trash2, RefreshCw, Search, Plus, User, Edit, X, Check } from 'lucide-react';
import { getGuestList, addGuest, updateGuest, deleteGuest } from '../lib/tables';
import GuestListUploader from './GuestListUploader';
import * as XLSX from 'xlsx';
import type { GuestListItem, GuestListUpload } from '../types/tables';

interface Props {
  registryId: string;
}

export default function GuestListManager({ registryId }: Props) {
  const [guests, setGuests] = useState<GuestListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploader, setShowUploader] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingGuest, setEditingGuest] = useState<GuestListItem | null>(null);
  const [newGuest, setNewGuest] = useState<GuestListUpload>({
    guest_name: ''
  });

  console.log('GuestListManager - Component rendering with state:', {
    showAddForm,
    showUploader,
    registryId
  });

  useEffect(() => {
    loadGuests();
  }, [registryId]);

  const loadGuests = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getGuestList(registryId);
      setGuests(data);
    } catch (err) {
      console.error('Error loading guest list:', err);
      setError('Failed to load guest list');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    // Create a new workbook
    const workbook = XLSX.utils.book_new();
    
    // Format data for export
    const exportData = guests.map(guest => ({
      'Guest Name': guest.guest_name,
      'Contact Info': guest.contact_info || '',
      'Dietary Restrictions': guest.dietary_restrictions || '',
      'Plus One Status': guest.plus_one_status ? 'Yes' : 'No',
      'Plus One Name': guest.plus_one_name || ''
    }));
    
    // Create a worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Guest List');
    
    // Generate Excel file
    XLSX.writeFile(workbook, 'guest_list.xlsx');
  };

  const handleAddGuest = async () => {
    if (!newGuest.guest_name.trim()) {
      setError('Guest name is required');
      return;
    }

    const result = await addGuest(registryId, newGuest);
    if (result.success) {
      setNewGuest({
        guest_name: ''
      });
      setShowAddForm(false);
      loadGuests();
    } else {
      setError(result.message);
    }
  };

  const handleEditGuest = async () => {
    if (!editingGuest) return;

    const result = await updateGuest(editingGuest.id, {
      guest_name: editingGuest.guest_name,
      contact_info: editingGuest.contact_info,
      dietary_restrictions: editingGuest.dietary_restrictions,
      plus_one_status: editingGuest.plus_one_status,
      plus_one_name: editingGuest.plus_one_name
    });

    if (result.success) {
      setEditingGuest(null);
      loadGuests();
    } else {
      setError(result.message);
    }
  };

  const handleDeleteGuest = async (guestId: string, guestName: string) => {
    if (!confirm(`Are you sure you want to remove ${guestName} from the guest list?`)) {
      return;
    }

    const result = await deleteGuest(guestId);
    if (result.success) {
      loadGuests();
    } else {
      setError(result.message);
    }
  };

  const filteredGuests = guests.filter(guest => 
    guest.guest_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (guest.contact_info && guest.contact_info.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Users className="w-5 h-5 text-white" />
          <h3 className="text-lg font-medium text-white">Guest List Management</h3>
        </div>
        <div className="flex items-center space-x-2">
          {guests.length > 0 && (
            <button
              onClick={handleExport}
              className="flex items-center space-x-1 px-3 py-1 text-sm bg-gray-800/50 rounded-md hover:bg-gray-700/50 text-gray-300 hover:text-white"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          )}
          <button
            onClick={loadGuests}
            className="flex items-center space-x-1 px-3 py-1 text-sm bg-gray-800/50 rounded-md hover:bg-gray-700/50 text-gray-300 hover:text-white"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center space-x-1 px-3 py-1 text-sm bg-green-600 rounded-md hover:bg-green-700 text-white"
          >
            <Plus className="w-4 h-4" />
            <span>Add Guest</span>
          </button>
          <button
            onClick={() => setShowUploader(!showUploader)}
            className="flex items-center space-x-1 px-3 py-1 text-sm bg-primary rounded-md hover:bg-primary-600 text-white"
          >
            {showUploader ? (
              <>
                <X className="w-4 h-4" />
                <span>Cancel Upload</span>
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                <span>Upload List</span>
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/50 border border-red-800 text-red-200 p-3 rounded-md">
          {error}
        </div>
      )}

      {/* Add Guest Form */}
      {showAddForm && (
        <div className="bg-gray-900/50 p-4 rounded-lg space-y-4">
          <h4 className="text-white font-medium">Add New Guest</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Guest Name *
              </label>
              <input
                type="text"
                value={newGuest.guest_name}
                onChange={(e) => setNewGuest({ ...newGuest, guest_name: e.target.value })}
                className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                placeholder="Enter guest name"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => {
                console.log('GuestListManager - Cancel Add Form clicked');
                setShowAddForm(false);
                setNewGuest({
                  guest_name: ''
                });
                setError('');
                console.log('GuestListManager - Add form state reset');
              }}
              className="px-4 py-2 text-gray-300 hover:text-white"
            >
              Cancel
            </button>
            <button
              onClick={handleAddGuest}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Add Guest
            </button>
          </div>
        </div>
      )}

      {showUploader ? (
        <GuestListUploader 
          registryId={registryId} 
          onUploadSuccess={() => {
            loadGuests();
            setShowUploader(false);
          }} 
        />
      ) : (
        <>
          {guests.length > 0 ? (
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                  placeholder="Search guests..."
                />
              </div>

              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                </div>
              ) : filteredGuests.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {filteredGuests.map((guest) => (
                    <div key={guest.id} className="bg-gray-900/50 p-4 rounded-lg">
                      {editingGuest?.id === guest.id ? (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-1">
                                Guest Name *
                              </label>
                              <input
                                type="text"
                                value={editingGuest.guest_name}
                                onChange={(e) => setEditingGuest({ ...editingGuest, guest_name: e.target.value })}
                                className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-1">
                                Contact Info
                              </label>
                              <input
                                type="text"
                                value={editingGuest.contact_info || ''}
                                onChange={(e) => setEditingGuest({ ...editingGuest, contact_info: e.target.value })}
                                className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-1">
                                Dietary Restrictions
                              </label>
                              <input
                                type="text"
                                value={editingGuest.dietary_restrictions || ''}
                                onChange={(e) => setEditingGuest({ ...editingGuest, dietary_restrictions: e.target.value })}
                                className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-1">
                                Plus One Name
                              </label>
                              <input
                                type="text"
                                value={editingGuest.plus_one_name || ''}
                                onChange={(e) => setEditingGuest({ ...editingGuest, plus_one_name: e.target.value })}
                                className="w-full px-3 py-2 bg-gray-800/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
                              />
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`edit-plus-one-${guest.id}`}
                              checked={editingGuest.plus_one_status}
                              onChange={(e) => setEditingGuest({ ...editingGuest, plus_one_status: e.target.checked })}
                              className="h-4 w-4 bg-gray-800 border-gray-700 rounded text-primary focus:ring-primary"
                            />
                            <label htmlFor={`edit-plus-one-${guest.id}`} className="text-sm text-gray-300">
                              Has Plus One
                            </label>
                          </div>
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => setEditingGuest(null)}
                              className="px-3 py-1 text-gray-300 hover:text-white"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleEditGuest}
                              className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <User className="w-5 h-5 text-gray-400 flex-shrink-0" />
                              <div>
                                <h4 className="font-medium text-white">{guest.guest_name}</h4>
                                <div className="text-sm text-gray-300 space-y-1">
                                  {guest.contact_info && (
                                    <p>Contact: {guest.contact_info}</p>
                                  )}
                                  {guest.dietary_restrictions && (
                                    <p>Dietary: {guest.dietary_restrictions}</p>
                                  )}
                                  {guest.plus_one_status && (
                                    <p>Plus One: {guest.plus_one_name || 'Yes'}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setEditingGuest(guest)}
                              className="text-blue-400 hover:text-blue-300"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteGuest(guest.id, guest.guest_name)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-900/50 rounded-lg">
                  <Users className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">No guests found matching your search</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-900/50 rounded-lg">
              <Users className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-white mb-2">No Guests Yet</h4>
              <p className="text-gray-400 mb-4">Upload a guest list or add guests manually</p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => setShowAddForm(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Add First Guest
                </button>
                <button
                  onClick={() => setShowUploader(true)}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600"
                >
                  Upload Guest List
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}